'use client';

import { c as _c } from "react/compiler-runtime";
import { useAssinaturaAcordoPainel } from '../../hooks/useAssinaturaAcordoPainel';
import { useConfirmacaoPausaRetomada } from '../../hooks/useConfirmacaoPausaRetomada';
import { useModalOfertaNotificacao } from '../../hooks/useModalOfertaNotificacao';
import type { NegociacaoDetailTab } from '../../types/negociacaoDetailTypes';
import { useNegociacaoDetailPage } from '../../hooks/useNegociacaoDetailPage';
import { ROTULO_DO_TIPO_DE_MIDIA } from '@/shared/constants';
import { useToast } from '@/shared/providers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useRouter, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { updateNegociacaoCaso } from '../../store/actions';
import { selectAssinaturaAcordoPainel, selectNegociacaoDetailIsUpdating } from '../../store/selectors';
import type { NegociacaoDocumentoUiItem, UpdateNegociacaoCasoInput } from '../../types/negociacaoDetailTypes';
import { buildOfertaNotificacaoHref, hasSituacaoSemResposta, shouldOfferNotificacao } from '../../utils/ofertaNotificacao';
import { extrairExtensao, isMensagemDeMidia } from '../../utils/negociacaoDetailUiUtils';
import { montarSecaoAcordo } from '../../utils/acordoExibido';
import type { MensagemMidiaTipo } from '@/features/messaging';
import { useAnexoComposer } from '@/features/messaging';
import { ROTA_DA_VISAO_ASSINATURAS, veioDaVisaoAssinaturas } from '@/shared/constants/navegacaoAssinaturas';
import { CanalAtivoBar } from '../tabs/chat/CanalAtivoBar';
import { CardDirecionamentoLexi } from '../tabs/chat/CardDirecionamentoLexi';
import { FeatherFileText, FeatherImage, FeatherMic, FeatherVideo } from '@subframe/core';
import { LazyLoader } from '@/shared/components/ui';
import { NegociacaoDetailSidebar } from '../sidebar/NegociacaoDetailSidebar';
import { NegociacaoDetailTabs } from '../tabs/NegociacaoDetailTabs';
import { NegociacaoDetailErrorState } from './NegociacaoDetailErrorState';
import { NegociacaoDetailHeader } from './NegociacaoDetailHeader';
import { NegociacaoDetailLoadingState } from './NegociacaoDetailLoadingState';
import { FeedbackButton } from '@/features/negociacaoFeedback';
const NegociacaoChatTab = dynamic(() => import('../tabs/chat/NegociacaoChatTab').then(m => m.NegociacaoChatTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const NegociacaoDocumentosTab = dynamic(() => import('../tabs/documentos/NegociacaoDocumentosTab').then(m => m.NegociacaoDocumentosTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const CertificadoNegociacaoTab = dynamic(() => import('../tabs/certificado/CertificadoNegociacaoTab').then(m => m.CertificadoNegociacaoTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const NegociacaoMarcosTab = dynamic(() => import('../tabs/linhaDoTempo/NegociacaoMarcosTab').then(m => m.NegociacaoMarcosTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const NegociacaoResumoTab = dynamic(() => import('../tabs/resumo/NegociacaoResumoTab').then(m => m.NegociacaoResumoTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const HistoricoTab = dynamic(() => import('../tabs/historico/HistoricoTab').then(m => m.HistoricoTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ModalEncerramentoNegociacao = dynamic(() => import('../modals/ModalEncerramentoNegociacao').then(m => m.ModalEncerramentoNegociacao), {
  loading: () => null,
  ssr: false
});
const ModalEncerrarAtendimento = dynamic(() => import('../modals/ModalEncerrarAtendimento').then(m => m.ModalEncerrarAtendimento), {
  loading: () => null,
  ssr: false
});
const AvisoMensagemRecebidaEncerrada = dynamic(() => import('./AvisoMensagemRecebidaEncerrada').then(m => m.AvisoMensagemRecebidaEncerrada), {
  loading: () => null,
  ssr: false
});
const ModalReaberturaNegociacao = dynamic(() => import('../modals/ModalReaberturaNegociacao').then(m => m.ModalReaberturaNegociacao), {
  loading: () => null,
  ssr: false
});
const ModalPausarRetomarNegociacao = dynamic(() => import('../modals/ModalPausarRetomarNegociacao').then(m => m.ModalPausarRetomarNegociacao), {
  loading: () => null,
  ssr: false
});
const ModalOfertaNotificacao = dynamic(() => import('../modals/ModalOfertaNotificacao').then(m => m.ModalOfertaNotificacao), {
  loading: () => null,
  ssr: false
});
const NegociacaoEditModal = dynamic(() => import('./NegociacaoEditModal').then(m => m.NegociacaoEditModal), {
  loading: () => null,
  ssr: false
});
const ConfirmAttachmentModal = dynamic(() => import('@/shared/components/ui/common/ConfirmAttachmentModal').then(m => m.ConfirmAttachmentModal), {
  loading: () => null,
  ssr: false
});
const parseTab = (value: string | null): NegociacaoDetailTab => {
  if (value === 'chat') return 'chat';
  if (value === 'linha_do_tempo') return 'linha_do_tempo';
  if (value === 'documentos') return 'documentos';
  if (value === 'certificado') return 'certificado';
  if (value === 'outras_negociacoes') return 'outras_negociacoes';
  return 'resumo';
};
interface NegociacaoDetailPageContainerProps {
  caseId: string;
}
export function NegociacaoDetailPageContainer(t0) {
  const $ = _c(268);
  const {
    caseId
  } = t0;
  const searchParams = useSearchParams();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    showSuccess,
    showError
  } = useToast();
  const isUpdating = useAppSelector(selectNegociacaoDetailIsUpdating);
  const assinaturaPainel = useAppSelector(selectAssinaturaAcordoPainel);
  let t1;
  if ($[0] !== searchParams) {
    t1 = parseTab(searchParams.get("tab"));
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialTab = t1;
  let t2;
  if ($[2] !== caseId || $[3] !== initialTab) {
    t2 = {
      caseId,
      initialTab
    };
    $[2] = caseId;
    $[3] = initialTab;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    activeTab,
    setActiveTab,
    telefones,
    telefonesLoading,
    telefonesError,
    carregarTelefones,
    canalVisualizadoId,
    selecionarCanal,
    isLoading,
    isLoadingAiInsights,
    aiInsightsError,
    error,
    retryLoad,
    negotiation,
    flow,
    isFlowLoading,
    caso,
    colunasExtras,
    dataAtualizacaoLabel,
    ultimaMensagemEmLabel,
    chatMessages,
    lastMensagemUi,
    situacaoAtualUi,
    iaStatusUi,
    conversaId,
    chatMessage,
    setChatMessage,
    handleSendMessage,
    isIAPaused,
    handleToggleAutomationClick,
    isToggleAutomationLoading,
    isReadOnly,
    canToggleAutomation,
    canSendMessage,
    sendDisabledReason,
    janelaStatus,
    canResumeWindow,
    janelaNuncaRespondeu,
    onResumeWindow,
    isResumingJanela,
    resumeJanelaError,
    hasPendingStart,
    handleCancelStart,
    handlePauseCaso,
    handleResumeCaso,
    handleEncerrarCaso,
    handleEncerrarAtendimento,
    handleReabrirCaso,
    isClosed,
    encerramento,
    isEncerrando,
    isEncerrandoAtendimento,
    isReabrindo,
    mensagemRecebidaAposEncerramento,
    handleEnviarMensagemUnica,
    mensagensForCaso,
    totalMensagens,
    isStartPending,
    isStartFailed,
    isStartCancelled,
    getTemperatureColorClass,
    outrasNegociacoes,
    outrasIsLoading,
    outrasError,
    outrasPage,
    outrasTotal,
    outrasTotalPages,
    hasOutrasNegociacoes,
    setOutrasPage
  } = useNegociacaoDetailPage(t2);
  useAssinaturaAcordoPainel(caso?.id, activeTab === "documentos");
  const chatScrollContainerRef = useRef(null);
  const chatBottomRef = useRef(null);
  const chatScrollTopRef = useRef(0);
  const shouldAutoScrollRef = useRef(true);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const behavior = t4 === undefined ? "smooth" : t4;
      const anchor = chatBottomRef.current;
      if (!anchor) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          anchor.scrollIntoView({
            block: "end",
            behavior
          });
        });
      });
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const scrollToBottom = t3;
  const [isEncerramentoModalOpen, setIsEncerramentoModalOpen] = useState(false);
  const [isEncerrarAtendimentoModalOpen, setIsEncerrarAtendimentoModalOpen] = useState(false);
  const [isReaberturaModalOpen, setIsReaberturaModalOpen] = useState(false);
  const t4 = negotiation?.isPaused ?? false;
  let t5;
  if ($[6] !== handlePauseCaso || $[7] !== handleResumeCaso || $[8] !== t4) {
    t5 = {
      isPaused: t4,
      onPausar: handlePauseCaso,
      onRetomar: handleResumeCaso
    };
    $[6] = handlePauseCaso;
    $[7] = handleResumeCaso;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const pausaRetomada = useConfirmacaoPausaRetomada(t5);
  let t6;
  if ($[10] !== caso) {
    t6 = shouldOfferNotificacao(caso);
    $[10] = caso;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const showOfertaNotificacao = t6;
  const {
    isAberto: isOfertaNotificacaoModalOpen,
    setIsAberto: setIsOfertaNotificacaoModalOpen
  } = useModalOfertaNotificacao(showOfertaNotificacao);
  let t7;
  if ($[12] !== caso) {
    t7 = !!caso && hasSituacaoSemResposta(caso);
    $[12] = caso;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const isSemResposta = t7;
  let t8;
  if ($[14] !== caso || $[15] !== router || $[16] !== showOfertaNotificacao) {
    t8 = () => {
      if (!caso || !showOfertaNotificacao) {
        return;
      }
      router.push(buildOfertaNotificacaoHref(caso));
    };
    $[14] = caso;
    $[15] = router;
    $[16] = showOfertaNotificacao;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleIniciarNotificacao = t8;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [mensagemAvisoDispensadaId, setMensagemAvisoDispensadaId] = useState(null);
  const mensagemAvisoId = caso && mensagemRecebidaAposEncerramento ? `${caso.id}:${mensagemRecebidaAposEncerramento.id}` : null;
  const isMensagemAvisoOpen = Boolean(mensagemAvisoId && mensagemAvisoId !== mensagemAvisoDispensadaId);
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsEditModalOpen(true);
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleEditData = t9;
  let t10;
  if ($[19] !== caso || $[20] !== dispatch || $[21] !== showError || $[22] !== showSuccess) {
    t10 = data => {
      if (!caso) {
        return;
      }
      dispatch(updateNegociacaoCaso({
        caseId: caso.id,
        data
      })).unwrap().then(() => {
        showSuccess("Dados atualizados", "Os dados da negocia\xE7\xE3o foram salvos.");
        setIsEditModalOpen(false);
      }).catch(err => {
        showError("Erro ao salvar", err);
      });
    };
    $[19] = caso;
    $[20] = dispatch;
    $[21] = showError;
    $[22] = showSuccess;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleSaveEdit = t10;
  let t11;
  if ($[24] !== setChatMessage) {
    t11 = () => setChatMessage("");
    $[24] = setChatMessage;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const limparChatAposAnexo = t11;
  const t12 = caso?.id ?? null;
  const t13 = caso?.counterpartyPhone ?? "";
  const t14 = !isIAPaused;
  let t15;
  if ($[26] !== chatMessage || $[27] !== conversaId || $[28] !== limparChatAposAnexo || $[29] !== t12 || $[30] !== t13 || $[31] !== t14) {
    t15 = {
      conversaId,
      caseId: t12,
      phone: t13,
      caption: chatMessage,
      shouldPauseIa: t14,
      onEnviado: limparChatAposAnexo
    };
    $[26] = chatMessage;
    $[27] = conversaId;
    $[28] = limparChatAposAnexo;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const anexoComposer = useAnexoComposer(t15);
  let t16;
  if ($[33] !== anexoComposer || $[34] !== handleSendMessage) {
    t16 = () => {
      if (anexoComposer.interceptouEnvio()) {
        return;
      }
      handleSendMessage();
    };
    $[33] = anexoComposer;
    $[34] = handleSendMessage;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const handleChatSend = t16;
  const acordoIdDoPainel = assinaturaPainel?.acordo?.id ?? null;
  caso?.acordos;
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FeatherImage />;
    t18 = <FeatherFileText />;
    t19 = <FeatherMic />;
    t20 = <FeatherVideo />;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
  } else {
    t17 = $[36];
    t18 = $[37];
    t19 = $[38];
    t20 = $[39];
  }
  let secaoAcordo;
  let t21;
  if ($[40] !== acordoIdDoPainel || $[41] !== caso?.acordos || $[42] !== mensagensForCaso) {
    const iconByType = {
      IMAGE: t17,
      DOCUMENT: t18,
      AUDIO: t19,
      VIDEO: t20
    };
    const mensagemDocs = mensagensForCaso.filter(isMensagemDeMidia).map(m => {
      const ext = m.mediaUrl ? extrairExtensao(m.mediaUrl) : "";
      const displayName = ext ? `${ROTULO_DO_TIPO_DE_MIDIA[m.tipo]}.${ext}` : `${ROTULO_DO_TIPO_DE_MIDIA[m.tipo]} - ${m.id.slice(0, 8)}`;
      return {
        id: m.id,
        name: displayName,
        createdAt: m.createdAt,
        icon: iconByType[m.tipo],
        url: m.mediaUrl || "",
        type: m.tipo,
        origem: m.direction === "OUTBOUND" ? "ESCRITORIO" : "CONTRAPARTE"
      };
    });
    let t22;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <FeatherFileText />;
      $[45] = t22;
    } else {
      t22 = $[45];
    }
    secaoAcordo = montarSecaoAcordo(caso?.acordos ?? [], acordoIdDoPainel, t22);
    t21 = [...mensagemDocs, ...secaoAcordo.documentos].sort(_temp);
    $[40] = acordoIdDoPainel;
    $[41] = caso?.acordos;
    $[42] = mensagensForCaso;
    $[43] = secaoAcordo;
    $[44] = t21;
  } else {
    secaoAcordo = $[43];
    t21 = $[44];
  }
  let t22;
  if ($[46] !== secaoAcordo.acordoIds || $[47] !== t21) {
    t22 = {
      documentos: t21,
      acordoIds: secaoAcordo.acordoIds
    };
    $[46] = secaoAcordo.acordoIds;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const {
    documentos,
    acordoIds
  } = t22;
  let t23;
  let t24;
  if ($[49] !== activeTab) {
    t23 = () => {
      if (activeTab !== "chat") {
        return;
      }
      shouldAutoScrollRef.current = true;
      requestAnimationFrame(() => {
        const el = chatScrollContainerRef.current;
        if (!el) {
          return;
        }
        el.scrollTop = shouldAutoScrollRef.current ? el.scrollHeight : chatScrollTopRef.current;
      });
    };
    t24 = [activeTab];
    $[49] = activeTab;
    $[50] = t23;
    $[51] = t24;
  } else {
    t23 = $[50];
    t24 = $[51];
  }
  useEffect(t23, t24);
  let t25;
  let t26;
  if ($[52] !== activeTab || $[53] !== chatMessages) {
    t25 = () => {
      if (activeTab !== "chat" || chatMessages.length === 0) {
        return;
      }
      if (!shouldAutoScrollRef.current) {
        return;
      }
      scrollToBottom();
    };
    t26 = [chatMessages, activeTab, scrollToBottom];
    $[52] = activeTab;
    $[53] = chatMessages;
    $[54] = t25;
    $[55] = t26;
  } else {
    t25 = $[54];
    t26 = $[55];
  }
  useEffect(t25, t26);
  let t27;
  if ($[56] !== activeTab || $[57] !== router || $[58] !== searchParams || $[59] !== setActiveTab) {
    t27 = value => {
      if (activeTab === "chat") {
        const el_0 = chatScrollContainerRef.current;
        if (el_0) {
          chatScrollTopRef.current = el_0.scrollTop;
        }
      }
      const nextTab = parseTab(value);
      setActiveTab(nextTab);
      const nextParams = new URLSearchParams(searchParams.toString());
      nextParams.set("tab", nextTab);
      router.replace(`?${nextParams.toString()}`);
    };
    $[56] = activeTab;
    $[57] = router;
    $[58] = searchParams;
    $[59] = setActiveTab;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  const handleTabChange = t27;
  let t28;
  if ($[61] !== router || $[62] !== searchParams) {
    t28 = () => {
      router.push(veioDaVisaoAssinaturas(searchParams) ? ROTA_DA_VISAO_ASSINATURAS : "/negociacoes");
    };
    $[61] = router;
    $[62] = searchParams;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  const handleBackToList = t28;
  let t29;
  if ($[64] !== router) {
    t29 = id => {
      router.push(`/negociacoes/${id}`);
    };
    $[64] = router;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  const handleVerOutraNegociacao = t29;
  let t30;
  if ($[66] !== router) {
    t30 = id_0 => {
      router.push(`/notificacoes/${id_0}`);
    };
    $[66] = router;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  const handleVerNotificacao = t30;
  if (isLoading) {
    let t31;
    if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <NegociacaoDetailLoadingState />;
      $[68] = t31;
    } else {
      t31 = $[68];
    }
    return t31;
  }
  if (error || !negotiation || !caso) {
    let t31;
    if ($[69] !== retryLoad) {
      t31 = <NegociacaoDetailErrorState onRetry={retryLoad} />;
      $[69] = retryLoad;
      $[70] = t31;
    } else {
      t31 = $[70];
    }
    return t31;
  }
  let t31;
  let t32;
  if ($[71] !== pausaRetomada) {
    t31 = () => pausaRetomada.abrir("pausar");
    t32 = () => pausaRetomada.abrir("retomar");
    $[71] = pausaRetomada;
    $[72] = t31;
    $[73] = t32;
  } else {
    t31 = $[72];
    t32 = $[73];
  }
  let t33;
  let t34;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setIsEncerramentoModalOpen(true);
    t34 = () => setIsReaberturaModalOpen(true);
    $[74] = t33;
    $[75] = t34;
  } else {
    t33 = $[74];
    t34 = $[75];
  }
  const t35 = isClosed && Boolean(encerramento?.permiteReabrir);
  const t36 = activeTab !== "resumo" || isReadOnly;
  let t37;
  if ($[76] !== getTemperatureColorClass || $[77] !== handleBackToList || $[78] !== handleCancelStart || $[79] !== hasPendingStart || $[80] !== isReadOnly || $[81] !== negotiation || $[82] !== t31 || $[83] !== t32 || $[84] !== t35 || $[85] !== t36) {
    t37 = <div className="flex shrink-0 flex-col items-start gap-0 overflow-hidden bg-white px-4 pt-4 sm:px-5 sm:pt-5"><NegociacaoDetailHeader negotiation={negotiation} isReadOnly={isReadOnly} hasPendingStart={hasPendingStart} onBackToList={handleBackToList} onEditData={handleEditData} onCancelStart={handleCancelStart} onPauseCaso={t31} onResumeCaso={t32} onOpenEncerramentoModal={t33} onOpenReaberturaModal={t34} isClosed={t35} showTemperature={t36} getTemperatureColorClass={getTemperatureColorClass} /></div>;
    $[76] = getTemperatureColorClass;
    $[77] = handleBackToList;
    $[78] = handleCancelStart;
    $[79] = hasPendingStart;
    $[80] = isReadOnly;
    $[81] = negotiation;
    $[82] = t31;
    $[83] = t32;
    $[84] = t35;
    $[85] = t36;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== caso || $[88] !== handleSaveEdit || $[89] !== isEditModalOpen || $[90] !== isUpdating) {
    t38 = <NegociacaoEditModal open={isEditModalOpen} onOpenChange={setIsEditModalOpen} caso={caso} isUpdating={isUpdating} onSave={handleSaveEdit} />;
    $[87] = caso;
    $[88] = handleSaveEdit;
    $[89] = isEditModalOpen;
    $[90] = isUpdating;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  const t39 = caso?.titulo || "";
  const t40 = caso?.atendimentoHumanoAtivo ?? false;
  let t41;
  if ($[92] !== handleEncerrarCaso || $[93] !== isEncerramentoModalOpen || $[94] !== isEncerrando || $[95] !== t39 || $[96] !== t40) {
    t41 = <ModalEncerramentoNegociacao open={isEncerramentoModalOpen} onOpenChange={setIsEncerramentoModalOpen} negociacaoNome={t39} temAtendimentoHumanoAtivo={t40} onEncerrar={handleEncerrarCaso} isEncerrando={isEncerrando} />;
    $[92] = handleEncerrarCaso;
    $[93] = isEncerramentoModalOpen;
    $[94] = isEncerrando;
    $[95] = t39;
    $[96] = t40;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] !== pausaRetomada) {
    t42 = open => {
      if (!open) {
        pausaRetomada.fechar();
      }
    };
    $[98] = pausaRetomada;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== negotiation.negotiationName || $[101] !== pausaRetomada.acao || $[102] !== pausaRetomada.confirmar || $[103] !== pausaRetomada.isConfirmando || $[104] !== t42) {
    t43 = <ModalPausarRetomarNegociacao acao={pausaRetomada.acao} negociacaoNome={negotiation.negotiationName} isConfirmando={pausaRetomada.isConfirmando} onOpenChange={t42} onConfirmar={pausaRetomada.confirmar} />;
    $[100] = negotiation.negotiationName;
    $[101] = pausaRetomada.acao;
    $[102] = pausaRetomada.confirmar;
    $[103] = pausaRetomada.isConfirmando;
    $[104] = t42;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] !== caso || $[107] !== handleEncerrarAtendimento || $[108] !== isEncerrandoAtendimento || $[109] !== isEncerrarAtendimentoModalOpen) {
    t44 = caso && <ModalEncerrarAtendimento open={isEncerrarAtendimentoModalOpen} onOpenChange={setIsEncerrarAtendimentoModalOpen} numeroNegociacao={caso.numeroNegociacao} contraparte={caso.counterpartyName} onConfirmar={handleEncerrarAtendimento} isEncerrando={isEncerrandoAtendimento} />;
    $[106] = caso;
    $[107] = handleEncerrarAtendimento;
    $[108] = isEncerrandoAtendimento;
    $[109] = isEncerrarAtendimentoModalOpen;
    $[110] = t44;
  } else {
    t44 = $[110];
  }
  let t45;
  if ($[111] !== caso || $[112] !== handleReabrirCaso || $[113] !== isClosed || $[114] !== isReaberturaModalOpen || $[115] !== isReabrindo) {
    t45 = isClosed && caso && <ModalReaberturaNegociacao open={isReaberturaModalOpen} onOpenChange={setIsReaberturaModalOpen} negociacaoNome={caso?.numeroNegociacao || ""} onReabrir={async (manterDados, justificativa) => {
      await handleReabrirCaso(manterDados, justificativa);
    }} isReabrindo={isReabrindo} />;
    $[111] = caso;
    $[112] = handleReabrirCaso;
    $[113] = isClosed;
    $[114] = isReaberturaModalOpen;
    $[115] = isReabrindo;
    $[116] = t45;
  } else {
    t45 = $[116];
  }
  let t46;
  if ($[117] !== handleIniciarNotificacao || $[118] !== isOfertaNotificacaoModalOpen || $[119] !== setIsOfertaNotificacaoModalOpen) {
    t46 = <ModalOfertaNotificacao open={isOfertaNotificacaoModalOpen} onOpenChange={setIsOfertaNotificacaoModalOpen} onIniciar={handleIniciarNotificacao} />;
    $[117] = handleIniciarNotificacao;
    $[118] = isOfertaNotificacaoModalOpen;
    $[119] = setIsOfertaNotificacaoModalOpen;
    $[120] = t46;
  } else {
    t46 = $[120];
  }
  let t47;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = () => setIsDetailsOpen(true);
    $[121] = t47;
  } else {
    t47 = $[121];
  }
  let t48;
  if ($[122] !== activeTab || $[123] !== handleTabChange || $[124] !== hasOutrasNegociacoes || $[125] !== isDetailsOpen || $[126] !== isReadOnly) {
    t48 = <NegociacaoDetailTabs activeTab={activeTab} onTabChange={handleTabChange} showOutrasNegociacoes={hasOutrasNegociacoes} isDetailsOpen={isDetailsOpen} isReadOnly={isReadOnly} onOpenDetails={t47} />;
    $[122] = activeTab;
    $[123] = handleTabChange;
    $[124] = hasOutrasNegociacoes;
    $[125] = isDetailsOpen;
    $[126] = isReadOnly;
    $[127] = t48;
  } else {
    t48 = $[127];
  }
  let t49;
  if ($[128] !== activeTab || $[129] !== aiInsightsError || $[130] !== caso || $[131] !== chatMessages || $[132] !== dataAtualizacaoLabel || $[133] !== getTemperatureColorClass || $[134] !== handleTabChange || $[135] !== iaStatusUi || $[136] !== isLoadingAiInsights || $[137] !== isReadOnly || $[138] !== lastMensagemUi || $[139] !== negotiation || $[140] !== situacaoAtualUi || $[141] !== totalMensagens || $[142] !== ultimaMensagemEmLabel) {
    t49 = activeTab === "resumo" && <NegociacaoResumoTab negotiation={negotiation} caso={caso} chatMessages={chatMessages} totalMensagens={totalMensagens} isReadOnly={isReadOnly} dataAtualizacaoLabel={dataAtualizacaoLabel} ultimaMensagemEmLabel={ultimaMensagemEmLabel} lastMensagemUi={lastMensagemUi} situacaoAtualUi={situacaoAtualUi} iaStatusUi={iaStatusUi} isLoadingAiInsights={isLoadingAiInsights} aiInsightsError={aiInsightsError} getTemperatureColorClass={getTemperatureColorClass} onOpenChatTab={() => handleTabChange("chat")} onOpenTimelineTab={() => handleTabChange("linha_do_tempo")} onOpenDetails={() => setIsDetailsOpen(true)} onOpenEncerrarAtendimentoModal={() => setIsEncerrarAtendimentoModalOpen(true)} />;
    $[128] = activeTab;
    $[129] = aiInsightsError;
    $[130] = caso;
    $[131] = chatMessages;
    $[132] = dataAtualizacaoLabel;
    $[133] = getTemperatureColorClass;
    $[134] = handleTabChange;
    $[135] = iaStatusUi;
    $[136] = isLoadingAiInsights;
    $[137] = isReadOnly;
    $[138] = lastMensagemUi;
    $[139] = negotiation;
    $[140] = situacaoAtualUi;
    $[141] = totalMensagens;
    $[142] = ultimaMensagemEmLabel;
    $[143] = t49;
  } else {
    t49 = $[143];
  }
  let t50;
  if ($[144] !== activeTab || $[145] !== anexoComposer.anexo || $[146] !== canResumeWindow || $[147] !== canSendMessage || $[148] !== canToggleAutomation || $[149] !== canalVisualizadoId || $[150] !== caso.atendimentoHumanoAtivo || $[151] !== caso.counterpartyPhone || $[152] !== chatMessage || $[153] !== chatMessages || $[154] !== encerramento?.data || $[155] !== encerramento?.motivo || $[156] !== encerramento?.permiteReabrir || $[157] !== encerramento?.tipo || $[158] !== handleChatSend || $[159] !== handleToggleAutomationClick || $[160] !== iaStatusUi || $[161] !== isClosed || $[162] !== isIAPaused || $[163] !== isReadOnly || $[164] !== isResumingJanela || $[165] !== isSemResposta || $[166] !== isToggleAutomationLoading || $[167] !== janelaNuncaRespondeu || $[168] !== janelaStatus || $[169] !== onResumeWindow || $[170] !== resumeJanelaError || $[171] !== selecionarCanal || $[172] !== sendDisabledReason || $[173] !== setChatMessage || $[174] !== setIsOfertaNotificacaoModalOpen || $[175] !== showOfertaNotificacao || $[176] !== telefones) {
    t50 = activeTab === "chat" && <div className="flex h-full min-h-0 flex-col gap-1"><CanalAtivoBar telefones={telefones} canalVisualizadoId={canalVisualizadoId} counterpartyPhone={caso.counterpartyPhone} onSelecionarCanal={selecionarCanal} /><NegociacaoChatTab chatMessages={chatMessages} automacao={{
        iaStatusUi,
        isIAPaused,
        isReadOnly,
        canToggle: canToggleAutomation,
        isToggleLoading: isToggleAutomationLoading,
        onToggle: handleToggleAutomationClick
      }} composer={{
        value: chatMessage,
        onChange: setChatMessage,
        canSend: canSendMessage,
        disabledReason: sendDisabledReason,
        onSend: handleChatSend
      }} janela={{
        status: janelaStatus,
        nuncaRespondeu: janelaNuncaRespondeu,
        canResume: canResumeWindow,
        onResume: onResumeWindow,
        isResuming: isResumingJanela,
        resumeError: resumeJanelaError
      }} anexo={anexoComposer.anexo} scroll={{
        containerRef: chatScrollContainerRef,
        bottomRef: chatBottomRef,
        scrollTopRef: chatScrollTopRef,
        shouldAutoScrollRef
      }} encerramento={{
        isClosed,
        isSemResposta,
        atendimentoHumanoAtivo: caso?.atendimentoHumanoAtivo ?? false,
        tipo: encerramento?.tipo,
        data: encerramento?.data,
        motivo: encerramento?.motivo,
        permiteReabrir: encerramento?.permiteReabrir ?? false,
        onReabrir: () => setIsReaberturaModalOpen(true)
      }} direcionamento={<CardDirecionamentoLexi />} showOfertaNotificacao={showOfertaNotificacao} onOfertaNotificacaoClick={() => setIsOfertaNotificacaoModalOpen(true)} /><div className="flex shrink-0 pt-2"><FeedbackButton /></div></div>;
    $[144] = activeTab;
    $[145] = anexoComposer.anexo;
    $[146] = canResumeWindow;
    $[147] = canSendMessage;
    $[148] = canToggleAutomation;
    $[149] = canalVisualizadoId;
    $[150] = caso.atendimentoHumanoAtivo;
    $[151] = caso.counterpartyPhone;
    $[152] = chatMessage;
    $[153] = chatMessages;
    $[154] = encerramento?.data;
    $[155] = encerramento?.motivo;
    $[156] = encerramento?.permiteReabrir;
    $[157] = encerramento?.tipo;
    $[158] = handleChatSend;
    $[159] = handleToggleAutomationClick;
    $[160] = iaStatusUi;
    $[161] = isClosed;
    $[162] = isIAPaused;
    $[163] = isReadOnly;
    $[164] = isResumingJanela;
    $[165] = isSemResposta;
    $[166] = isToggleAutomationLoading;
    $[167] = janelaNuncaRespondeu;
    $[168] = janelaStatus;
    $[169] = onResumeWindow;
    $[170] = resumeJanelaError;
    $[171] = selecionarCanal;
    $[172] = sendDisabledReason;
    $[173] = setChatMessage;
    $[174] = setIsOfertaNotificacaoModalOpen;
    $[175] = showOfertaNotificacao;
    $[176] = telefones;
    $[177] = t50;
  } else {
    t50 = $[177];
  }
  let t51;
  if ($[178] !== activeTab || $[179] !== caso.dataCriacao || $[180] !== caso.encerramento?.data || $[181] !== caso.id) {
    t51 = activeTab === "linha_do_tempo" && <NegociacaoMarcosTab casoId={caso?.id} dataCriacao={caso?.dataCriacao} dataEncerramento={caso?.encerramento?.data} />;
    $[178] = activeTab;
    $[179] = caso.dataCriacao;
    $[180] = caso.encerramento?.data;
    $[181] = caso.id;
    $[182] = t51;
  } else {
    t51 = $[182];
  }
  let t52;
  if ($[183] !== acordoIds || $[184] !== activeTab || $[185] !== assinaturaPainel || $[186] !== caso.id || $[187] !== documentos) {
    t52 = activeTab === "documentos" && <NegociacaoDocumentosTab casoId={caso.id} documentos={documentos} acordoIds={acordoIds} assinaturaPainel={assinaturaPainel} />;
    $[183] = acordoIds;
    $[184] = activeTab;
    $[185] = assinaturaPainel;
    $[186] = caso.id;
    $[187] = documentos;
    $[188] = t52;
  } else {
    t52 = $[188];
  }
  let t53;
  if ($[189] !== activeTab || $[190] !== caso || $[191] !== negotiation.status) {
    t53 = activeTab === "certificado" && <CertificadoNegociacaoTab key={caso.id} caso={caso} statusLabel={negotiation.status} />;
    $[189] = activeTab;
    $[190] = caso;
    $[191] = negotiation.status;
    $[192] = t53;
  } else {
    t53 = $[192];
  }
  let t54;
  if ($[193] !== activeTab || $[194] !== caso.counterpartyName || $[195] !== handleVerNotificacao || $[196] !== handleVerOutraNegociacao || $[197] !== outrasError || $[198] !== outrasIsLoading || $[199] !== outrasNegociacoes || $[200] !== outrasPage || $[201] !== outrasTotal || $[202] !== outrasTotalPages || $[203] !== setOutrasPage) {
    t54 = activeTab === "outras_negociacoes" && <HistoricoTab items={outrasNegociacoes} nomeAtual={caso?.counterpartyName ?? null} isLoading={outrasIsLoading} error={outrasError} page={outrasPage} totalPages={outrasTotalPages} total={outrasTotal} onPageChange={setOutrasPage} onVerNegociacao={handleVerOutraNegociacao} onVerNotificacao={handleVerNotificacao} />;
    $[193] = activeTab;
    $[194] = caso.counterpartyName;
    $[195] = handleVerNotificacao;
    $[196] = handleVerOutraNegociacao;
    $[197] = outrasError;
    $[198] = outrasIsLoading;
    $[199] = outrasNegociacoes;
    $[200] = outrasPage;
    $[201] = outrasTotal;
    $[202] = outrasTotalPages;
    $[203] = setOutrasPage;
    $[204] = t54;
  } else {
    t54 = $[204];
  }
  let t55;
  if ($[205] !== t49 || $[206] !== t50 || $[207] !== t51 || $[208] !== t52 || $[209] !== t53 || $[210] !== t54) {
    t55 = <main className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-neutral-50/50">{t49}{t50}{t51}{t52}{t53}{t54}</main>;
    $[205] = t49;
    $[206] = t50;
    $[207] = t51;
    $[208] = t52;
    $[209] = t53;
    $[210] = t54;
    $[211] = t55;
  } else {
    t55 = $[211];
  }
  const t56 = caso.start?.agentInput ?? null;
  let t57;
  if ($[212] !== caso.situacoes) {
    t57 = caso.situacoes ?? [];
    $[212] = caso.situacoes;
    $[213] = t57;
  } else {
    t57 = $[213];
  }
  let t58;
  if ($[214] !== canToggleAutomation || $[215] !== carregarTelefones || $[216] !== colunasExtras || $[217] !== flow || $[218] !== getTemperatureColorClass || $[219] !== handleToggleAutomationClick || $[220] !== iaStatusUi || $[221] !== isDetailsOpen || $[222] !== isFlowLoading || $[223] !== isIAPaused || $[224] !== isReadOnly || $[225] !== isStartCancelled || $[226] !== isStartFailed || $[227] !== isStartPending || $[228] !== isToggleAutomationLoading || $[229] !== negotiation || $[230] !== t56 || $[231] !== t57 || $[232] !== telefones || $[233] !== telefonesError || $[234] !== telefonesLoading) {
    t58 = <NegociacaoDetailSidebar open={isDetailsOpen} onOpenChange={setIsDetailsOpen} negotiation={negotiation} colunasExtras={colunasExtras} flow={flow} isFlowLoading={isFlowLoading} iaStatusUi={iaStatusUi} isIAPaused={isIAPaused} isReadOnly={isReadOnly} canToggleAutomation={canToggleAutomation} isToggleAutomationLoading={isToggleAutomationLoading} isStartPending={isStartPending} isStartFailed={isStartFailed} isStartCancelled={isStartCancelled} agentInput={t56} situacoes={t57} telefones={telefones} telefonesLoading={telefonesLoading} telefonesError={telefonesError} onCarregarTelefones={carregarTelefones} onToggleAutomationClick={handleToggleAutomationClick} getTemperatureColorClass={getTemperatureColorClass} />;
    $[214] = canToggleAutomation;
    $[215] = carregarTelefones;
    $[216] = colunasExtras;
    $[217] = flow;
    $[218] = getTemperatureColorClass;
    $[219] = handleToggleAutomationClick;
    $[220] = iaStatusUi;
    $[221] = isDetailsOpen;
    $[222] = isFlowLoading;
    $[223] = isIAPaused;
    $[224] = isReadOnly;
    $[225] = isStartCancelled;
    $[226] = isStartFailed;
    $[227] = isStartPending;
    $[228] = isToggleAutomationLoading;
    $[229] = negotiation;
    $[230] = t56;
    $[231] = t57;
    $[232] = telefones;
    $[233] = telefonesError;
    $[234] = telefonesLoading;
    $[235] = t58;
  } else {
    t58 = $[235];
  }
  let t59;
  if ($[236] !== t55 || $[237] !== t58) {
    t59 = <div className="flex min-h-0 w-full flex-1 gap-4 overflow-hidden p-3 sm:p-4">{t55}{t58}</div>;
    $[236] = t55;
    $[237] = t58;
    $[238] = t59;
  } else {
    t59 = $[238];
  }
  let t60;
  if ($[239] !== caso || $[240] !== encerramento?.permiteReabrir || $[241] !== handleEnviarMensagemUnica || $[242] !== handleTabChange || $[243] !== isMensagemAvisoOpen || $[244] !== mensagemAvisoId || $[245] !== mensagemRecebidaAposEncerramento) {
    t60 = caso && mensagemRecebidaAposEncerramento && <AvisoMensagemRecebidaEncerrada open={isMensagemAvisoOpen} negociacaoNome={caso.titulo} mensagemRecebida={mensagemRecebidaAposEncerramento} permiteReabrir={Boolean(encerramento?.permiteReabrir)} onReabrir={async () => {
      setIsReaberturaModalOpen(true);
    }} onResponderUmaVez={handleEnviarMensagemUnica} onVerConversa={() => {
      handleTabChange("chat");
      setMensagemAvisoDispensadaId(mensagemAvisoId);
    }} onIgnorar={() => setMensagemAvisoDispensadaId(mensagemAvisoId)} />;
    $[239] = caso;
    $[240] = encerramento?.permiteReabrir;
    $[241] = handleEnviarMensagemUnica;
    $[242] = handleTabChange;
    $[243] = isMensagemAvisoOpen;
    $[244] = mensagemAvisoId;
    $[245] = mensagemRecebidaAposEncerramento;
    $[246] = t60;
  } else {
    t60 = $[246];
  }
  let t61;
  if ($[247] !== anexoComposer.anexo.pending || $[248] !== anexoComposer.cancelarEnvio || $[249] !== anexoComposer.confirmarEnvio || $[250] !== anexoComposer.erroEnvio || $[251] !== anexoComposer.isEnviando || $[252] !== anexoComposer.isModalAberto || $[253] !== anexoComposer.setModalAberto || $[254] !== chatMessage) {
    t61 = anexoComposer.anexo.pending && <ConfirmAttachmentModal open={anexoComposer.isModalAberto} onOpenChange={anexoComposer.setModalAberto} fileName={anexoComposer.anexo.pending.name} fileSizeLabel={anexoComposer.anexo.pending.sizeLabel} fileTipo={anexoComposer.anexo.pending.tipo} caption={chatMessage} isBusy={anexoComposer.isEnviando} errorMessage={anexoComposer.erroEnvio} onConfirm={anexoComposer.confirmarEnvio} onCancel={anexoComposer.cancelarEnvio} />;
    $[247] = anexoComposer.anexo.pending;
    $[248] = anexoComposer.cancelarEnvio;
    $[249] = anexoComposer.confirmarEnvio;
    $[250] = anexoComposer.erroEnvio;
    $[251] = anexoComposer.isEnviando;
    $[252] = anexoComposer.isModalAberto;
    $[253] = anexoComposer.setModalAberto;
    $[254] = chatMessage;
    $[255] = t61;
  } else {
    t61 = $[255];
  }
  let t62;
  if ($[256] !== t37 || $[257] !== t38 || $[258] !== t41 || $[259] !== t43 || $[260] !== t44 || $[261] !== t45 || $[262] !== t46 || $[263] !== t48 || $[264] !== t59 || $[265] !== t60 || $[266] !== t61) {
    t62 = <div className="flex h-full min-h-[560px] flex-col gap-0">{t37}{t38}{t41}{t43}{t44}{t45}{t46}{t48}{t59}{t60}{t61}</div>;
    $[256] = t37;
    $[257] = t38;
    $[258] = t41;
    $[259] = t43;
    $[260] = t44;
    $[261] = t45;
    $[262] = t46;
    $[263] = t48;
    $[264] = t59;
    $[265] = t60;
    $[266] = t61;
    $[267] = t62;
  } else {
    t62 = $[267];
  }
  return t62;
}
function _temp(a, b) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}